type ToastType = 'info' | 'success' | 'error' | 'warning' | 'notification';

const CONTAINER_ID = 'app-toast-container';

function ensureContainer() {
  let container = document.getElementById(CONTAINER_ID);
  if (!container) {
    container = document.createElement('div');
    container.id = CONTAINER_ID;
    container.setAttribute('aria-live', 'polite');
    container.style.position = 'fixed';
    container.style.right = '1rem';
    container.style.top = '1rem';
    container.style.zIndex = '9999';
    container.style.display = 'flex';
    container.style.flexDirection = 'column';
    container.style.alignItems = 'flex-end';
    container.style.gap = '0.5rem';
    container.style.maxWidth = '40rem';
    document.body.appendChild(container);
  }
  return container;
}

function createIcon(type: ToastType) {
  const wrapper = document.createElement('span');
  wrapper.style.display = 'inline-flex';
  wrapper.style.alignItems = 'center';
  wrapper.style.justifyContent = 'center';
  wrapper.style.width = '1.5rem';
  wrapper.style.height = '1.5rem';
  wrapper.style.marginRight = '0.6rem';

  const svgNS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(svgNS, 'svg');
  svg.setAttribute('width', '20');
  svg.setAttribute('height', '20');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');
  svg.style.display = 'block';

  const path = document.createElementNS(svgNS, 'path');
  path.setAttribute('stroke-linecap', 'round');
  path.setAttribute('stroke-linejoin', 'round');

  switch (type) {
    case 'success':
      path.setAttribute('d', 'M5 13l4 4L19 7');
      svg.style.color = '#ecfdf5';
      break;
    case 'error':
      path.setAttribute('d', 'M6 18L18 6M6 6l12 12');
      svg.style.color = '#fee2e2';
      break;
    case 'warning':
      path.setAttribute('d', 'M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4M12 17h.01');
      svg.style.color = '#fff7ed';
      break;
    case 'notification':
      path.setAttribute('d', 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5');
      svg.style.color = '#111827';
      break;
    default:
      path.setAttribute('d', 'M13 16h-1v-4h-1m1-4h.01');
      svg.style.color = '#bfdbfe';
  }

  svg.appendChild(path);
  wrapper.appendChild(svg);
  return wrapper;
}

export function showToast(message: string, type: ToastType = 'info', duration = 4000) {
  const container = ensureContainer();
  const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

  const toast = document.createElement('div');
  toast.setAttribute('role', 'status');
  toast.setAttribute('aria-live', 'polite');
  toast.style.display = 'flex';
  toast.style.alignItems = 'center';
  toast.style.background = '#111827';
  toast.style.color = '#fff';
  toast.style.padding = '0.6rem 0.75rem';
  toast.style.borderRadius = '0.75rem';
  toast.style.boxShadow = '0 6px 18px rgba(0,0,0,0.12)';
  toast.style.maxWidth = '36rem';
  toast.style.overflow = 'hidden';
  toast.style.gap = '0.5rem';
  toast.style.transition = 'transform 200ms ease, opacity 200ms ease';
  toast.style.transform = 'translateY(-8px)';
  toast.style.opacity = '0';
  toast.id = id;

  // color background per type
  switch (type) {
    case 'success':
      toast.style.background = '#065f46';
      break;
    case 'error':
      toast.style.background = '#b91c1c';
      break;
    case 'warning':
      toast.style.background = '#92400e';
      break;
    case 'notification':
      toast.style.background = '#ffffff';
      toast.style.color = '#111827';
      toast.style.border = '1px solid #e5e7eb';
      break;
    default:
      toast.style.background = '#1e3a8a';
      break;
  }

  const content = document.createElement('div');
  content.style.display = 'flex';
  content.style.alignItems = 'center';
  content.style.flex = '1 1 auto';
  content.style.minWidth = '0';

  const icon = createIcon(type);
  content.appendChild(icon);

  const text = document.createElement('div');
  text.style.fontSize = '0.95rem';
  text.style.lineHeight = '1.2';
  text.style.overflow = 'hidden';
  text.style.textOverflow = 'ellipsis';
  text.style.whiteSpace = 'nowrap';
  text.textContent = message;
  content.appendChild(text);

  const closeBtn = document.createElement('button');
  closeBtn.setAttribute('aria-label', 'Dismiss notification');
  closeBtn.style.background = 'transparent';
  closeBtn.style.border = 'none';
  closeBtn.style.color = 'inherit';
  closeBtn.style.cursor = 'pointer';
  closeBtn.style.marginLeft = '0.5rem';
  closeBtn.style.display = 'inline-flex';
  closeBtn.style.alignItems = 'center';
  closeBtn.style.justifyContent = 'center';
  closeBtn.style.width = '1.75rem';
  closeBtn.style.height = '1.75rem';
  closeBtn.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 18L18 6M6 6l12 12"/></svg>`;

  closeBtn.onclick = () => {
    dismissToast(id);
  };

  toast.appendChild(content);
  toast.appendChild(closeBtn);

  // prepend so newest appear on top
  if (container.firstChild) container.insertBefore(toast, container.firstChild);
  else container.appendChild(toast);

  // trigger enter animation
  requestAnimationFrame(() => {
    toast.style.transform = 'translateY(0)';
    toast.style.opacity = '1';
  });

  const timeout = setTimeout(() => dismissToast(id), duration);

  // pause on hover
  toast.addEventListener('mouseenter', () => clearTimeout(timeout));
  toast.addEventListener('mouseleave', () => setTimeout(() => dismissToast(id), 1200));

  return id;
}

export function dismissToast(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  el.style.opacity = '0';
  el.style.transform = 'translateY(-8px)';
  setTimeout(() => { el.remove(); }, 220);
}

export function clearAllToasts() {
  const container = document.getElementById(CONTAINER_ID);
  if (!container) return;
  Array.from(container.children).forEach((c) => c.remove());
}

export default showToast;
